import React, { useState } from "react";
import { View, Alert } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { LinearGradient } from "expo-linear-gradient";
import { Check } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import { useMenstrualHealth } from "../MenstrualHealthContext";
import {
  BaseCompleteScreen,
  navigateToNextSection,
  getNextSectionButtonLabel,
} from "@src/packages/medical-history/components/BaseCompleteScreen";
import { useMedicalHistoryTheme } from "../../../theme/MedicalHistoryTheme";

function CelebrationIllustration({ size = 200 }: { size?: number }) {
  const theme = useMedicalHistoryTheme();
  return (
    <View
      style={{ width: size, height: size }}
      className="items-center justify-center"
    >
      {/* Main circle with check */}
      <LinearGradient
        colors={theme.completeGradient}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{
          width: size * 0.45,
          height: size * 0.45,
          borderRadius: size * 0.225,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Check
          size={size * 0.22}
          color={theme.completeButtonBg}
          strokeWidth={3}
        />
      </LinearGradient>

      {/* Decorative dots */}
      <View
        className="absolute bg-pink-400"
        style={{
          width: size * 0.1,
          height: size * 0.1,
          borderRadius: size * 0.05,
          top: size * 0.1,
          left: size * 0.2,
        }}
      />
      <View
        className="absolute bg-purple-400"
        style={{
          width: size * 0.08,
          height: size * 0.08,
          borderRadius: size * 0.04,
          top: size * 0.15,
          right: size * 0.18,
        }}
      />
      <View
        className="absolute bg-green-400"
        style={{
          width: size * 0.12,
          height: size * 0.12,
          borderRadius: size * 0.06,
          bottom: size * 0.12,
          left: size * 0.15,
        }}
      />
      <View
        className="absolute bg-violet-300"
        style={{
          width: size * 0.07,
          height: size * 0.07,
          borderRadius: size * 0.035,
          bottom: size * 0.2,
          right: size * 0.15,
          opacity: 0.8,
        }}
      />
      <View
        className="absolute bg-pink-300"
        style={{
          width: size * 0.06,
          height: size * 0.06,
          borderRadius: size * 0.03,
          top: size * 0.38,
          left: size * 0.1,
        }}
      />
      <View
        className="absolute bg-emerald-400"
        style={{
          width: size * 0.05,
          height: size * 0.05,
          borderRadius: size * 0.025,
          top: size * 0.35,
          right: size * 0.12,
        }}
      />
    </View>
  );
}

export function MenstrualHealthCompleteScreen() {
  const t = useTranslations();
  const navigation = useNavigation();
  const { save } = useMenstrualHealth();
  const [isSaving, setIsSaving] = useState(false);

  const handleFinish = async () => {
    setIsSaving(true);
    try {
      await save();
      navigateToNextSection(navigation, "menstrualHealth");
    } catch {
      Alert.alert(
        t("med_history.save_failed_title"),
        t("med_history.save_failed_message"),
        [{ text: t("med_history.save_failed_ok") }],
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <BaseCompleteScreen
      subtitle={t("med_history.gynae.menstrual_health.complete_subtitle")}
      buttonLabel={getNextSectionButtonLabel(t, "menstrualHealth")}
      isSubmitting={isSaving}
      onFinish={handleFinish}
      illustration={<CelebrationIllustration size={200} />}
    />
  );
}
